import type { NavigateOptions, RegisteredRouter } from "@tanstack/react-router";
import {
  createMemoryHistory,
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  RouterProvider,
  Link as TanStackLink,
  useLocation as useTanStackLocation,
  useNavigate as useTanStackNavigate,
  useParams as useTanStackParams,
} from "@tanstack/react-router";
import type React from "react";
import { useCallback, useEffect, useMemo } from "react";

type LegacyNavigateOptions = {
  replace?: boolean;
  state?: unknown;
};

type NavigateTarget = string | number | NavigateOptions<RegisteredRouter>;

export function useNavigate() {
  const navigate = useTanStackNavigate();
  return useCallback(
    (to: NavigateTarget, options: LegacyNavigateOptions = {}) => {
      if (typeof to === "number") {
        window.history.go(to);
        return;
      }
      if (typeof to === "object") {
        return navigate(to);
      }
      return navigate({
        to,
        replace: options.replace,
        state: options.state as never,
      });
    },
    [navigate],
  );
}

export function useParams<
  T extends Record<string, string | undefined> = Record<
    string,
    string | undefined
  >,
>() {
  return useTanStackParams({
    strict: false,
    structuralSharing: true,
  } as never) as T;
}

export function useLocation(): {
  pathname: string;
  search: string;
  state: unknown | null;
  hash: string;
  key: string;
} {
  const location = useTanStackLocation();
  const {
    __TSR_index: _index,
    __TSR_key: routerKey,
    __hashScrollIntoViewOptions: _hashOptions,
    key: _legacyKey,
    ...state
  } = location.state;
  return {
    ...location,
    search: location.searchStr,
    hash: location.hash
      ? location.hash.startsWith("#")
        ? location.hash
        : `#${location.hash}`
      : "",
    state,
    key: String(routerKey ?? ""),
  };
}

type LinkProps = Omit<React.ComponentProps<typeof TanStackLink>, "to"> & {
  to: string;
};

export function Link({ to, ...props }: LinkProps) {
  return <TanStackLink {...props} to={to} />;
}

export function Navigate({ to, replace }: { to: string; replace?: boolean }) {
  const navigate = useTanStackNavigate();
  useEffect(() => {
    void navigate({ to, replace });
  }, [navigate, replace, to]);
  return null;
}

export { Outlet };

export function MemoryRouter({
  children,
  initialEntries = ["/"],
  initialState,
}: {
  children: React.ReactNode;
  initialEntries?: string[];
  initialState?: unknown;
}) {
  const router = useMemo(() => {
    const history = createMemoryHistory({ initialEntries });
    if (initialState !== undefined) {
      history.replace(initialEntries[0] ?? "/", initialState as never);
    }
    const rootRoute = createRootRoute({ component: Outlet });
    const testRoute = createRoute({
      getParentRoute: () => rootRoute,
      path: "$",
      component: () => <>{children}</>,
    });
    const indexRoute = createRoute({
      getParentRoute: () => rootRoute,
      path: "/",
      component: () => <>{children}</>,
    });
    return createRouter({
      routeTree: rootRoute.addChildren([indexRoute, testRoute]),
      history,
    });
  }, [children, initialEntries, initialState]);
  return <RouterProvider router={router} />;
}
